/**
 * Tokyo Reads — cart drawer history handling (custom, 2026-07; v2 2026-09-19, kaart #62).
 * Makes the OS back gesture close the cart drawer instead of leaving the page.
 *
 * Relies on the theme's own events only (no theme.js changes):
 *   - `theme:cart:open`  — dispatched by cart-element.openCartDrawer()
 *   - `theme:cart:close` — dispatched first thing in cart-element.closeCartDrawer()
 *
 * v2 (2026-09-19): the popstate that belongs to OUR history entry is swallowed
 * (stopImmediatePropagation) before it reaches the theme. Palo Alto's filters.js
 * re-renders the collection on every popstate (onHistoryChange -> renderSection ->
 * mobileFiltersScrollLock) and that forces the filter drawer OPEN on mobile — so
 * closing the cart on a collection page opened the filters every time. Our entry
 * never changes the URL, the grid already matches the state underneath it, so the
 * theme has nothing to re-render for these pops. Listener registered with capture
 * and from head.liquid (before the section scripts), so it runs first on both
 * registration-order and capture-first engines.
 *
 * Load from layout/theme.liquid (snippets/head.liquid) with:
 *   <script src="{{ 'tr-drawer-history.js' | asset_url }}" defer="defer"></script>
 */
(function () {
  'use strict';

  // Touch screens only (Jan, 2026-09-19): the back gesture is how a phone
  // dismisses an overlay; on desktop nobody closes a modal with the browser
  // back button, so there we stay out of the history stack entirely.
  var isTouch = false;
  try {
    isTouch = window.matchMedia('(pointer: coarse)').matches;
  } catch (e) {
    isTouch = false;
  }
  if (!isTouch) {
    return;
  }

  var STATE_KEY = 'trCartDrawer';
  // Set while we consume our own history entry via history.back(), so the
  // resulting popstate is not treated as a user back-press.
  var suppressNextPop = false;
  // True only while OUR entry sits on top of the history stack (pushState
  // succeeded and was not consumed yet). Pops are swallowed only then: if the
  // push failed (Safari rate limit) a back-press pops a real entry and the theme
  // must see it, e.g. to restore the grid to the previous filter state.
  var entryActive = false;

  function getCartDrawer() {
    return document.querySelector('cart-element[data-cart-drawer]');
  }

  function drawerIsOpen() {
    var el = getCartDrawer();
    return !!el && el.classList.contains('is-open');
  }

  function stateIsOurs(state) {
    return !!(state && state[STATE_KEY]);
  }

  function swallow(e) {
    // Keep this pop away from the theme (filters.js would re-render and open the
    // mobile filter drawer). Only for pops that belong to our own entry.
    if (e && typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
  }

  document.addEventListener('theme:cart:open', function () {
    // Guard against double-push when open fires twice (e.g. first open goes
    // through renderCartDrawer -> openCartDrawer again).
    if (!stateIsOurs(window.history.state)) {
      try {
        window.history.pushState({ trCartDrawer: true }, '');
        entryActive = true;
      } catch (e) {
        /* Safari rate-limits pushState; drawer still works, back just navigates. */
        entryActive = false;
      }
    }
  });

  function report(method) {
    // Measurement hook (kaart #62): the theme's GA4 wiring listens for this and
    // sends cart_drawer_close {method: 'back' | 'x'}; after 4 weeks the share of
    // 'back' decides whether this script stays.
    try {
      document.dispatchEvent(new CustomEvent('tr:cart:close', { bubbles: true, detail: { method: method } }));
    } catch (e) {
      /* old engines without CustomEvent constructor: no measurement, no harm */
    }
  }

  document.addEventListener('theme:cart:close', function () {
    // Closed via X / overlay / ESC while our entry is current: consume it so a
    // later back-press is a real navigation again. When the close was caused
    // by popstate, the entry is already gone and this is a no-op.
    if (stateIsOurs(window.history.state)) {
      report('x');
      suppressNextPop = true;
      window.history.back();
    }
  });

  window.addEventListener('popstate', function (e) {
    if (suppressNextPop) {
      // Our own history.back() from the close handler above.
      suppressNextPop = false;
      entryActive = false;
      swallow(e);
      return;
    }
    if (drawerIsOpen()) {
      // User back-press while the cart drawer is open. Only when our entry was
      // really on top is this pop ours to swallow; otherwise the theme must
      // handle it (real navigation) and we merely close the drawer.
      if (entryActive) {
        swallow(e);
      }
      entryActive = false;
      report('back');
      var el = getCartDrawer();
      if (el && typeof el.closeCartDrawer === 'function') {
        // The history entry is already popped at this point, so the close
        // handler above will not call history.back() again.
        el.closeCartDrawer();
      }
    }
    // Drawer closed and the state is ours (forward-button onto a stale entry,
    // or reload while the drawer was open): do nothing — pressing back again
    // simply continues through history.
  }, true);

  // Measurement (kaart #62, themasessie 2, 19-09): forward the hook to GA4.
  // The shop's GA4 runs inside the Google channel web pixel (no gtag on the
  // page), so we publish a Shopify custom event that a custom pixel
  // (Settings > Customer events, code in runbook par 10) subscribes to and
  // sends as cart_drawer_close {method}. If a page-level gtag ever exists it
  // gets the same event directly.
  document.addEventListener('tr:cart:close', function (e) {
    var method = (e && e.detail && e.detail.method) || 'x';
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('cart_drawer_close', { method: method });
      }
    } catch (err) { /* no analytics: no measurement, no harm */ }
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'cart_drawer_close', { method: method });
      }
    } catch (err) { /* idem */ }
  });
})();
